import React, { useContext, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import DContext from "../../context/DContext";
import { authenticationService } from "../../services/authentication.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import ProfileBanks from "../../components/profile/ProfileBanks";
import { fileUrl } from "../../components/common/AccountSections";
import {
  Badge,
  DETAIL_ACCENTS,
  HeroChip,
  IconBox,
  ProfileHero,
  detailLabelStyle,
  isEmpty,
  show,
  titleCase,
} from "../../components/common/DetailUI";

const formatDate = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : null);

const Profile = () => {
  const { authState, authDispatch } = useContext(DContext);
  const { user, userProfile: profile, walletInfo: wallet, bankDetails } = authState;
  const [sending, setSending] = useState(false);

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || user?.name;
  const location = [profile?.city?.name, profile?.state?.name, profile?.country?.name].filter(Boolean).join(", ");
  const money = (v) =>
    isEmpty(v) ? "-" : `${wallet?.currency_code || ""} ${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`.trim();

  const banks = bankDetails || [];
  const groups = [
    {
      title: "Account", icon: "bi-person-vcard-fill", accent: DETAIL_ACCENTS.indigo,
      items: [
        { label: "Name", value: user?.name },
        { label: "User Code", value: user?.code },
        { label: "Role", value: user?.role?.role_type ? titleCase(user.role.role_type) : null },
        { label: "Status", value: user?.status, badge: true },
        { label: "Email", value: user?.email },
        { label: "Phone", value: user?.phone },
        { label: "Last Login", value: formatDate(user?.last_login) },
        //{ label: "Created At", value: formatDate(user?.created_at) },
      ],
    },
    {
      title: "Company", icon: "bi-building", accent: DETAIL_ACCENTS.sky,
      items: [
        { label: "Agency Name", value: profile?.agency_name },
        { label: "Supplier Code", value: profile?.supplier_code },
        { label: "Custom Code", value: profile?.custom_code },
        //{ label: "Sales Reference", value: profile?.sales_reference },
        //{ label: "Head Person", value: profile?.head_person },
        { label: "Contact Person", value: fullName },
        { label: "Gender", value: profile?.gender ? titleCase(profile.gender) : null },
        { label: "Website", value: profile?.website_url },
        //{ label: "Profile Status", value: profile?.status, badge: true },
        //{ label: "Inventory Status", value: profile?.inventory_status, badge: true },
        //{ label: "Remarks", value: profile?.remarks, full: true },
      ],
    },
    {
      title: "Contact & Address", icon: "bi-geo-alt-fill", accent: DETAIL_ACCENTS.teal,
      items: [
        { label: "Email", value: profile?.email },
        { label: "Mobile", value: profile?.mobile },
        { label: "WhatsApp", value: profile?.whatsapp_number },
        { label: "Pincode", value: profile?.pincode },
        { label: "City", value: profile?.city?.name },
        { label: "State", value: profile?.state?.name },
        { label: "Country", value: profile?.country?.name },
        { label: "Address", value: profile?.address, full: true },
      ],
    },
    {
      title: "Wallet", icon: "bi-wallet2", accent: DETAIL_ACCENTS.green,
      items: [
        { label: "Wallet Type", value: wallet?.wallet_type ? titleCase(wallet.wallet_type) : null },
        //{ label: "Currency", value: wallet?.currency_code },
        //{ label: "Status", value: wallet?.status, badge: true },
        { label: "Available Balance", value: money(wallet?.available_balance) },
        { label: "Current Balance", value: money(wallet?.current_balance) },
        { label: "Hold Balance", value: money(wallet?.hold_balance) },
      ],
    },
  ];

  // keep the stored list in sync after an edit; a new primary clears the flag on the others
  const handleBankUpdated = (updated, madePrimary) => {
    authDispatch({
      type: "BANKS_LOADED",
      payload: banks.map((b) => (b.id === updated.id ? { ...b, ...updated } : madePrimary ? { ...b, is_primary: false } : b)),
    });
  };

  const sendResetLink = async () => {
    if (!user?.email) {
      toast.error("No email found for this account");
      return;
    }
    try {
      setSending(true);
      const res = await authenticationService.changePasswordLink({ email: user.email, method: "get-email-link" });
      if (res?.success) {
        withReactContent(Swal).fire({ title: "Success", text: res.message, icon: "success" });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      withReactContent(Swal).fire({ title: "Error", text: SOMETHING_WENT_WRONG, icon: "error" });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section profile">
      <div className="mb-3">
        <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>
          My Profile
        </h4>
        <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
          <Link to="/dashboard" style={{ color: "#475569" }}>
            <i className="bi bi-house-door"></i>
          </Link>
          <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
          <span className="fw-semibold" style={{ color: "#4a5568" }}>Profile</span>
        </nav>
      </div>

      <ProfileHero
        logo={fileUrl(profile?.logo_url)}
        icon="bi-person-circle"
        title={profile?.agency_name || fullName}
        action={
          <button
            type="button"
            onClick={sendResetLink}
            disabled={sending}
            className="btn btn-sm d-flex align-items-center gap-2"
            style={{ borderRadius: 8, height: 36, padding: "0 16px", fontSize: 13, fontWeight: 700, background: "#fff", color: "#000", border: "none", boxShadow: "0 2px 8px rgba(15,23,42,.18)" }}
          >
            <i className="bi bi-shield-lock-fill"></i> {sending ? "Sending..." : "Send Password Reset Link"}
          </button>
        }
        // chips={
        //   <>
        //     {user?.role?.role_type && <HeroChip icon="bi-shield-check">{titleCase(user.role.role_type)}</HeroChip>}
        //     {profile?.supplier_code && <HeroChip icon="bi-upc">{profile.supplier_code.toUpperCase()}</HeroChip>}
        //     {user?.code && <HeroChip icon="bi-person-badge">{user.code.toUpperCase()}</HeroChip>}
        //   </>
        // }
        meta={[
          { icon: "bi-person", text: fullName },
          { icon: "bi-envelope", text: user?.email || profile?.email, href: `mailto:${user?.email || profile?.email}` },
          { icon: "bi-telephone", text: user?.phone || profile?.mobile },
          //{ icon: "bi-geo-alt", text: location },
        ]}
      />

      <div className="card border-0 shadow-sm" style={{ borderRadius: 14 }}>
        <div className="card-body p-3 p-md-4">
          {groups.map((g, gi) => (
            <div key={g.title} className={gi ? "mt-4" : ""}>
              <div className="d-flex align-items-center gap-2 pb-2 mb-3" style={{ borderBottom: `2px solid ${g.accent}26` }}>
                <IconBox icon={g.icon} accent={g.accent} size={26} />
                <span className="fw-bold" style={{ fontSize: 14, color: "#0f172a" }}>{g.title}</span>
              </div>
              <div className="row g-3">
                {g.items.map((f) => (
                  <div key={f.label} className={f.full ? "col-12" : "col-6 col-md-4 col-xl-3"}>
                    <div style={detailLabelStyle}>{f.label}</div>
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: isEmpty(f.value) ? "#cbd5e1" : "#0f172a", wordBreak: "break-word" }}>
                      {f.badge ? <Badge value={f.value} /> : show(f.value)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="mt-4">
            <div className="d-flex align-items-center gap-2 pb-2 mb-3" style={{ borderBottom: `2px solid ${DETAIL_ACCENTS.pink}26` }}>
              <IconBox icon="bi-bank2" accent={DETAIL_ACCENTS.pink} size={26} />
              <span className="fw-bold" style={{ fontSize: 14, color: "#0f172a" }}>Bank Details</span>
              <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{banks.length} account{banks.length == 1 ? "" : "s"}</span>
            </div>
            <ProfileBanks banks={banks} onUpdated={handleBankUpdated} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Profile;
